import { EventTypes, ModulesStates, VisibilityStates } from '@racemap/utilities/consts/events';
import { OneMinuteInMillis, OneSecondInMillis } from '@racemap/utilities/consts/time';
import { Spin } from 'antd';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useNavigate, useParams } from 'react-router';
import { validateUserIsAuthorized } from 'src/components/Safemap/utils/validateUserIsAuthorized';
import { UnauthorizedError } from 'src/lib/Errors';
import { useCurrentUser } from 'src/lib/customHooks';
import { useInterval } from 'usehooks-ts';
import { useMonitorStore } from '../store/monitor/monitorStore';

type Params = {
  eventSlug: string;
};

export function MonitorView() {
  const { loadData, loadLiveData, resetStore } = useMonitorStore((s) => s.actions);
  const { eventSlug } = useParams<Params>();
  const currentUser = useCurrentUser();
  const event = useMonitorStore((s) => s.event);
  const isLoading = useMonitorStore((s) => s.isLoadingData);
  const isAuthorized = validateUserIsAuthorized(currentUser, event);
  const navigate = useNavigate();

  if (event != null && event.type === EventTypes.SAFEMAP) {
    navigate(`/safemap/${event.id}`, { replace: true });
  }

  useEffect(() => {
    if (event == null || currentUser == null) return;
    if (!isAuthorized) {
      throw new UnauthorizedError();
    }
  }, [event, currentUser, isAuthorized]);

  useEffect(() => {
    if (eventSlug == null) return;
    if (event != null) {
      resetStore();
    }
    loadData(eventSlug);
  }, [eventSlug]);

  useInterval(async () => {
    if (eventSlug == null || !isAuthorized) return;
    loadData(eventSlug);
  }, OneMinuteInMillis);

  useInterval(async () => {
    if (!isAuthorized) return;
    loadLiveData();
  }, OneSecondInMillis * 10);

  if (isLoading || event == null) return <Spin size="large" />;

  if (event.visibility === VisibilityStates.ARCHIVED) {
    return <div>This Monitor has been archived.</div>;
  }
  if (event.modules.monitor.state !== ModulesStates.ACTIVATED) {
    return <div>This Monitor is disabled.</div>;
  }

  return (
    <>
      <MonitorViewHelmet eventName={event.name || event.id} />
      <Outlet />
    </>
  );
}

const MonitorViewHelmet: React.FC<{ eventName: string }> = ({ eventName }) => {
  return (
    <Helmet>
      <title>Racemap | Monitor {eventName}</title>
      <meta name="description" content="Monitor your event live with Racemap." />
      <meta name="robots" content="noindex,nofollow" />
    </Helmet>
  );
};
